import { Fab as MuiFab } from '@mui/material';
import { forwardRef } from 'react';

import { useColorMode } from '../theme/ColorMode.js';
import { elevation, mdSysColor, radius, typography } from '../theme/tokens.js';
import { Icon } from './Icon.js';

/**
 * FSE Fab — MD3 floating action button.
 *
 * Spec: `Documentation/Claude design files/preview/fab.html`. MD3 sizes: a
 * 56dp default (`md`) and a 96dp `lg`; passing `label` produces the extended
 * FAB (auto-width, 56dp tall, leading icon + display-family label). Level-3
 * shadow throughout. Default `primary` uses `primaryContainer`/
 * `onPrimaryContainer`; `tertiary` uses the mango `tertiaryContainer`.
 *
 * Wraps MUI `Fab`. Owns the public prop surface — consumers never touch MUI's
 * `sx`. Exposed through @fse/ui so consumers never import @mui/material
 * directly (swap-promise).
 */
export interface FabProps {
  /** Material Symbols ligature name (e.g. "add"). */
  icon: string;
  /** When provided, renders the extended FAB with this trailing label. */
  label?: string;
  onClick: () => void;
  /** 56dp default (`md`) or 96dp `lg`. Ignored when `label` is set (extended is 56dp). */
  size?: 'md' | 'lg';
  /** Container colour role. */
  color?: 'primary' | 'tertiary';
}

export const Fab = forwardRef<HTMLButtonElement, FabProps>(function Fab(
  { icon, label, onClick, size = 'md', color = 'primary' },
  ref,
) {
  const extended = label != null;
  const large = size === 'lg' && !extended;

  // Mode-aware: container roles tint correctly in both modes. In light they
  // resolve to the same brand values used before (primaryContainer=leaf[100],
  // onPrimaryContainer=leaf[900], tertiaryContainer=mango[100],
  // onTertiaryContainer=mango[800]).
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];
  const bg = color === 'tertiary' ? sys.tertiaryContainer : sys.primaryContainer;
  const fg = color === 'tertiary' ? sys.onTertiaryContainer : sys.onPrimaryContainer;

  const dimension = large ? 96 : 56;
  const corner = large ? radius.xxl : radius.xl;
  const iconSize = large ? 36 : 24;

  return (
    <MuiFab
      ref={ref}
      onClick={onClick}
      variant={extended ? 'extended' : 'circular'}
      aria-label={extended ? undefined : icon}
      sx={{
        boxShadow: elevation.level3,
        backgroundColor: bg,
        color: fg,
        textTransform: 'none',
        borderRadius: `${corner}px`,
        '&:hover': { backgroundColor: bg },
        ...(extended
          ? {
              height: 56,
              px: 2.5,
              gap: 1.25,
              fontFamily: typography.fontFamilyDisplay,
              fontSize: 16,
              fontWeight: 700,
            }
          : {
              width: dimension,
              height: dimension,
              minHeight: dimension,
            }),
      }}
    >
      <Icon name={icon} size={iconSize} color="inherit" />
      {extended ? label : null}
    </MuiFab>
  );
});
